import { useEffect, useMemo, useState } from 'react';

import { useAtlassianAccountMigrationStageQuery } from './AtlassianAccountMigrationStageQuery.generated';
import { requiresAaOnboardingTemplates as templates } from 'app/src/components/AtlassianAccountMigration/requiresAaOnboardingTemplates';
import { HYGIENE_MESSAGE_ID } from 'app/src/components/EmailHygieneWizard/emailHygieneOneTimeMessageIds';

import { AtlassianAccountMigrationStage as Stage } from './types';

interface Options {
  skip?: boolean;
}

export function useAtlassianAccountMigrationStage({
  skip = false,
}: Options = {}) {
  const [stage, setStage] = useState<Stage | null>(null);

  // eslint-disable-next-line @trello/no-apollo-refetch
  const { data, refetch } = useAtlassianAccountMigrationStageQuery({
    skip,
    variables: { memberId: 'me' },
  });

  const me = data?.member;

  const { template } = me?.requiresAaOnboarding || {};

  const hasCompletedHygiene = useMemo(() => {
    return me?.oneTimeMessagesDismissed?.includes(HYGIENE_MESSAGE_ID);
  }, [me?.oneTimeMessagesDismissed]);

  useEffect(() => {
    if (!me) {
      return;
    }

    if (me.isAaMastered) {
      switch (template) {
        case templates.ENTERPRISE:
          setStage(Stage.enterpriseMigration);
          break;
        case templates.INACTIVE:
          setStage(Stage.inactiveMigration);
          break;
        case templates.NEWLY_MANAGED:
          setStage(Stage.newlyManaged);
          break;
        default:
          if (hasCompletedHygiene) {
            setStage(Stage.migrationSuccess);
          } else {
            setStage(Stage.done);
          }
      }
    } else if (me.idEnterprise) {
      setStage(Stage.ineligible);
    } else if (hasCompletedHygiene) {
      setStage(Stage.migration);
    } else if (me.confirmed) {
      setStage(Stage.emailHygiene);
    } else {
      setStage(Stage.confirmEmail);
    }
  }, [
    me,
    me?.confirmed,
    me?.idEnterprise,
    me?.isAaMastered,
    hasCompletedHygiene,
    template,
  ]);

  return { stage, me, refetchStage: refetch };
}
